/**
 * COMMS-14: the Rust sync engine correctly emits `sync:status` on every cycle
 * outcome (offline/auth_error/idle/pushing/pulling/paused/auth_required —
 * see src-tauri/src/sync/engine.rs's emit_status call sites), but until this
 * store existed the only JS consumer (useSyncEngine.js's statusRef) wrote the
 * payload into a ref and nothing ever read it back — a workspace could be
 * silently broken (bad token, permanently-rejected batch, revoked access)
 * for days with zero visible indication.
 *
 * This store makes the same events reactive so a UI element can subscribe,
 * and debounces the "problem" flag so a normal few-seconds-offline blip
 * doesn't flash a banner for something that resolves on its own — only
 * `offline`/`auth_error` that PERSISTS for PROBLEM_DEBOUNCE_MS counts.
 */
import { create } from 'zustand';
import { addSessionEndedListener } from '../services/channelService';

const PROBLEM_STATES = new Set(['offline', 'auth_error']);
export const PROBLEM_DEBOUNCE_MS = 10000;

// workspaceId -> pending debounce timer. Module-level (not store state) since
// timer handles aren't serializable/comparable state; the store only ever
// reflects the *result* of a timer firing (or being cancelled).
const problemTimers = new Map();

function clearProblemTimer(workspaceId) {
  const t = problemTimers.get(workspaceId);
  if (t) {
    clearTimeout(t);
    problemTimers.delete(workspaceId);
  }
}

export const useSyncStatusStore = create((set, get) => ({
  // workspaceId -> { state, pending, lastError, updatedAt }
  byWorkspace: {},
  // workspaceId -> the status entry, present only once a problem state has
  // persisted past the debounce window. Absence means "no known problem",
  // NOT "definitely healthy" (an unauthenticated/not-yet-enrolled scope
  // never emits at all) — consumers should treat this as "problems we know
  // about", not a full health readout.
  problems: {},

  // COMMS-12: workspaceId -> { clockSkewMs, updatedAt }, fed by
  // sync:clock-skew. Not debounced like `problems` — the server only sends
  // this once its own >2min skew-warn threshold is already crossed
  // (clock_skew_ms/2 in sync.ex), so a single occurrence is already a real
  // signal, not a blip to filter. Not a PROBLEM_STATE either: it's an
  // advisory (sync itself keeps working), so it never blocks or delays the
  // offline/auth_error indicator above.
  clockSkew: {},

  /** Feed a raw `sync:clock-skew` payload ({ workspaceId, clockSkewMs }). */
  setClockSkew: (payload) => {
    const workspaceId = payload?.workspaceId;
    const clockSkewMs = payload?.clockSkewMs;
    if (!workspaceId || typeof clockSkewMs !== 'number') return;
    set((s) => ({
      clockSkew: { ...s.clockSkew, [workspaceId]: { clockSkewMs, updatedAt: Date.now() } },
    }));
  },

  /** Feed a raw `sync:status` payload ({ workspaceId, state, pending, lastError }). */
  setStatus: (payload) => {
    const workspaceId = payload?.workspaceId;
    if (!workspaceId) return;
    // Capture BEFORE overwriting byWorkspace below, so we can tell a
    // transition-into-problem apart from a repeat emission of one already
    // in progress.
    const prevEntry = get().byWorkspace[workspaceId];
    const wasAlreadyProblem = !!prevEntry && PROBLEM_STATES.has(prevEntry.state);
    const now = Date.now();
    // When the scope was last seen settled (idle, nothing waiting) — the
    // Documents sidebar footer's "Synced · 2 min ago" (card 1.8). Carried
    // forward across every other state so an offline spell keeps the time.
    const settled = payload.state === 'idle' && !payload.pending;
    const entry = {
      state: payload.state,
      pending: payload.pending,
      lastError: payload.lastError ?? null,
      updatedAt: now,
      lastOkAt: settled ? now : prevEntry?.lastOkAt ?? null,
    };
    set((s) => ({ byWorkspace: { ...s.byWorkspace, [workspaceId]: entry } }));

    if (PROBLEM_STATES.has(entry.state)) {
      // Only (re)start the debounce timer on the transition INTO a problem
      // state. The sync engine's retry-with-backoff loop re-emits the same
      // 'offline'/'auth_error' status on every retry (every
      // error_backoff_secs(n) — 1s, 2s, 4s, 8s, 16s...); clearing and
      // restarting the timer on each of those repeats — as this used to do
      // unconditionally — kept pushing the reveal time out for as long as
      // retries kept landing inside the debounce window, so the indicator
      // could take multiples of PROBLEM_DEBOUNCE_MS to appear for a
      // workspace that had in fact been broken continuously since the first
      // emission. A workspace already known to be in a problem state (or
      // with a timer already pending) leaves the existing timer alone.
      if (!wasAlreadyProblem && !problemTimers.has(workspaceId)) {
        problemTimers.set(
          workspaceId,
          setTimeout(() => {
            problemTimers.delete(workspaceId);
            // The state may have moved on again while the timer was pending —
            // re-read the latest entry rather than closing over the stale one.
            const latest = get().byWorkspace[workspaceId];
            if (latest && PROBLEM_STATES.has(latest.state)) {
              set((s) => ({ problems: { ...s.problems, [workspaceId]: latest } }));
            }
          }, PROBLEM_DEBOUNCE_MS),
        );
      }
    } else {
      clearProblemTimer(workspaceId);
      if (workspaceId in get().problems) {
        set((s) => {
          const next = { ...s.problems };
          delete next[workspaceId];
          return { problems: next };
        });
      }
    }
  },

  /** Logout / account switch: stop tracking every scope from the departed session. */
  reset: () => {
    problemTimers.forEach((t) => clearTimeout(t));
    problemTimers.clear();
    set({ byWorkspace: {}, problems: {}, clockSkew: {} });
  },
}));

// COMMS-14's reset() used to be called only from inside authStore.logout()
// (the hard path) — the soft (401 interceptor) and any other teardown path
// never reached it, leaving a departed session's "sync is broken" banner
// state and pending debounce timers alive for the next signed-in account.
// Subscribing here means this store resets ITSELF on every session end,
// hard or soft, instead of depending on a specific logout function to
// remember to call it.
addSessionEndedListener(() => {
  useSyncStatusStore.getState().reset();
});
